import { FC, useEffect, useState } from 'react';
import { IonButton, IonLabel, IonSegmentButton } from '@ionic/react';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { CaremapNotesModelFactory } from '../model/CaremapNotesModelFactory';
import { useSubscription } from '@meraki-internal/state';
import { createUseStyles } from 'react-jss';
import { ViewNoteMenu } from './ViewNoteMenu';
import { NoteDateTarget } from '../note-list/CareMapNotesListTitle';
import { UserNotes } from './UserNotes';
import { Overview } from './Overview';
import { SmartTasks } from './SmartTasks';
import { Attachments } from './Attachments';
import { DetailedSummary } from './DetailedSummary';
import { Transcription } from './Transcription';
import { Participants } from './Participants';
import { CareMapNoteTracker } from '../model/CareMapNoteTracker';
import { NoteShareHook } from './NoteShareHook';
import { SharingAndAccessState } from '../../share-caremap/sharing-and-access/SharingAndAccessState';
import { ArrowUpFromBracketIcon, ShareNodesIcon } from '../../theme/Icons';
import { isPlatform } from '../../utils/isPlatform';
import { BeaCommentBubble } from './BeaCommentBubble';
import { AppointmentLink } from './AppointmentLink';
import { AppointmentsState } from '../../appointments/AppointmentsState';
import { IAppointment } from '../../appointments/IAppointment';
import { TagsSection } from './TagsSection';
import { SummaryFeedback } from './SummaryFeedback';
import { IOSTabs } from '../../components/IOSTabs';
import { RecordingPlayer } from './RecordingPlayer';
import { MixPanelEventEmitter } from '../../metrics/MixPanelEventEmitter';
import { CareMapState } from '../../caremap/CareMapState';
import { NoteInsightCards } from '../../insights/NoteInsightCards';
import { IRecording } from '../../recording/RecordingState';
import { useResolvedNote } from './useResolvedNote';
import { InlineLoadError } from '../../components/InlineLoadError';
import { Text } from '../../components/Text';

type NoteDetailTab = 'summary' | 'recording';

const useStyles = createUseStyles({
    stretchHeight: {
        display: 'flex',
        flexDirection: 'column'
    },

    emptyState: {
        padding: 24,
        textAlign: 'center'
    }
});

interface INoteDetailBodyProps {
    noteId: string;
    trackingFrom?: string;
    editHandler: () => void;
    onAppointmentClick: (appointment: IAppointment) => void;
    onCloseDrawer: () => void;
    // Called once the server confirms the note is gone. A host that can navigate away (the care map
    // drawer pops back to the notes list) passes this; one with nowhere to go omits it and the
    // explanation below is rendered instead.
    onNoteUnavailable?: () => void;
}

export const NoteDetailBody: FC<INoteDetailBodyProps> = ({ noteId, trackingFrom, editHandler, onAppointmentClick, onCloseDrawer, onNoteUnavailable }) => {
    const classes = useStyles();

    const tracker = useInstance(CareMapNoteTracker);
    const appointmentsState = useInstance(AppointmentsState);
    const tracking = useInstance(MixPanelEventEmitter);
    const careMapState = useInstance(CareMapState);
    const sharingState = useInstance(SharingAndAccessState);

    const notesFactory = useInstance(CaremapNotesModelFactory);
    useSubscription(() => notesFactory);

    const model = notesFactory.forActiveCaremap();
    const { note, resolution, retry } = useResolvedNote(noteId);

    // Get appointment if linked through recording
    const appointmentId = note?.otherAttributes?.recording?.appointmentId;
    const appointment = appointmentId ? appointmentsState.getAppointment(appointmentId) : undefined;

    const [activeTab, setActiveTab] = useState<NoteDetailTab>('summary');

    // Keyed on the resolved note's id, not the requested one: a note reached by deep link may not be
    // in the list on first render and only arrives once the targeted fetch lands, and keying on the
    // requested id would fire this once against no note and never again, silently dropping those
    // views out of the metric.
    useEffect(() => {
        if (note) {
            tracker.onNoteViewed(note, trackingFrom);
        }
    }, [note?.noteId]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (resolution === 'unavailable') {
            onNoteUnavailable?.();
        }
    }, [resolution]); // eslint-disable-line react-hooks/exhaustive-deps

    if (!note) {
        if (resolution === 'error') {
            return (
                <div className={classes.emptyState} data-type="view-note-error">
                    <InlineLoadError
                        dataId="view-note-load-error"
                        message="Couldn't load this note. Check your connection and try again."
                        onRetry={retry}
                    />
                </div>
            );
        }

        if (resolution === 'unavailable') {
            return (
                <div className={classes.emptyState} data-type="view-note-unavailable">
                    <Text>This note is no longer available. It may have been deleted, or made private by its author.</Text>
                </div>
            );
        }

        // Still resolving: render nothing rather than flash an empty state the fetch is about to
        // replace.
        return null;
    }

    const canEdit = model.canEdit(note);

    const handleAppointmentClick = () => {
        if (appointment) {
            onAppointmentClick(appointment);
        }
    };

    const recording = note.otherAttributes?.recording as IRecording | undefined;
    // Presence of a CDN url is the only signal we use to decide whether to show
    // the tabs — recordings still being processed (status: 'uploaded' /
    // 'transcribed') already have a playable url and getting to the audio
    // earlier is a feature. Failed states (e.g. 'summarize-failed') also have
    // a url, and showing the player there is fine: the audio plays, the
    // summary tab just won't have AI content.
    const hasPlayableRecording = !!recording?.url;

    const handleTabChange = (tab: NoteDetailTab) => {
        if (tab === activeTab) {
            return;
        }
        setActiveTab(tab);
        tracking.track('Note Detail Tab Switched', () => ({
            tab,
            noteId: note.noteId,
            careMapId: careMapState.getActiveCaremap()?.careMapId
        }));
    };

    const showSummary = !hasPlayableRecording || activeTab === 'summary';
    const showRecording = hasPlayableRecording && activeTab === 'recording';

    return (
        <div data-type="view-note" data-note-id={note.noteId} className={classes.stretchHeight}>

            <div style={{display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between'}}>
                <div style={{fontSize: 20, fontWeight: 'bold', marginBottom: 6}}>{note.title}</div>
                <div style={{display: 'flex', alignItems: 'center'}}>
                    {canEdit && (
                        <IonButton fill="clear" size="small" data-type="note-share-button" aria-label="Share" onClick={() => sharingState.open('Note Share Hook', { startOnAddPerson: true })}>
                            {isPlatform('android') ? <ShareNodesIcon color="primary" /> : <ArrowUpFromBracketIcon color="primary" />}
                        </IonButton>
                    )}
                    <ViewNoteMenu model={model} noteId={noteId} trackingFrom={trackingFrom} onCloseDrawer={onCloseDrawer} customEditHandler={editHandler} />
                </div>
            </div>

            <NoteDateTarget note={note} showNoteTargetHeader showBothDates />

            <NoteShareHook note={note} />

            {hasPlayableRecording && (
                // The negative bottom margin pulls the tabs toward the summary/recording they switch:
                // that section carries its own top margin, so a tight bottom keeps the tabs grouped
                // with the content below rather than reading as attached to the sharing row above.
                <div style={{ margin: '24px 0 -8px' }} data-id="note-detail-tabs">
                    <IOSTabs<NoteDetailTab> value={activeTab} onChange={handleTabChange}>
                        <IonSegmentButton value="summary" data-id="note-detail-tab-summary">
                            <IonLabel>Summary</IonLabel>
                        </IonSegmentButton>
                        <IonSegmentButton value="recording" data-id="note-detail-tab-recording">
                            <IonLabel>Recording</IonLabel>
                        </IonSegmentButton>
                    </IOSTabs>
                </div>
            )}

            {showSummary && (
                <>
                    {note.otherAttributes?.recording?.summaryPrompt !== 'intro' && (
                        <UserNotes
                            content={note.html}
                            editHandler={canEdit ? editHandler : undefined}
                            hasAISummary={!!note.otherAttributes?.recording?.summary?.detailedSummary}
                        />
                    )}

                    {appointment && (
                        <AppointmentLink
                            appointment={appointment}
                            onClick={handleAppointmentClick}
                        />
                    )}

                    {/* The bubble fully self-gates (renders null when there is nothing to show),
                        so it mounts unconditionally — a second mount-level copy of that gate here
                        drifted from the bubble's own and had to be patched in both places. */}
                    <BeaCommentBubble note={note} onCloseDrawer={onCloseDrawer} />

                    <NoteInsightCards noteId={note.noteId} />

                    {note.otherAttributes?.recording?.summaryPrompt === 'intro' && (
                        <UserNotes
                            content={note.html}
                            editHandler={canEdit ? editHandler : undefined}
                            hasAISummary={!!note.otherAttributes?.recording?.summary?.detailedSummary}
                        />
                    )}

                    <Overview note={note} />

                    {note.otherAttributes?.recording?.summaryPrompt !== 'intro' && (
                        <Participants note={note} />
                    )}

                    <DetailedSummary note={note} />

                    <SmartTasks note={note} />

                    <SummaryFeedback note={note} />

                    <Attachments
                        note={note}
                        editHandler={canEdit ? editHandler : undefined}
                    />

                    <TagsSection note={note} />

                    {!hasPlayableRecording && <Transcription note={note} />}
                </>
            )}

            {showRecording && recording && (
                <>
                    <RecordingPlayer recording={recording} noteId={note.noteId} />
                    <Transcription note={note} />
                </>
            )}
        </div>
    );
};
